import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { dichiarazioneAgenzia, euro, MOTIVI_SCARTO, STATI_IMMOBILE, statoImmobile } from '../../utils/scheda';
import { giornoEMese, leggiDataUtc, linkChiama, migliaia } from '../../utils/percorso';
import { datiImmobile, perInserimento, prezzoMq } from '../../utils/immobili';
import EsitoTelefonata from '../staff/EsitoTelefonata';
import Icona from '../cliente/Icone';
import { errore } from './comuni';
import { useInvioUnico } from '../accesso/campi';
import { ParereImmobileDialog } from '../features/ParereImmobileDialog';
import { ritirato, testoRitirato, venduto, testoVenduto, variazioniPrezzo } from '../../utils/prezzoZona';
import Eliminazione from '../staff/Eliminazione';
import AzioniVisita from '../staff/AzioniVisita';
import { indicazioni, rigaVisita, visitaDa } from '../../utils/visite';
import CompilaDati from './CompilaDati';
import { AnnunciNuovi, CaseCondivise, CaseInArchivio, RicercheAutomatiche } from './Ricerche';

// Scheda "Immobili" della scheda cliente dello staff: nuovi annunci della ricerca automatica, case in archivio, l'avviso
// delle case attive anche per un altro cliente, le card grandi nell'ordine di inserimento (il più vecchio in alto; i
// filtri tolgono, non riordinano), ricerche automatiche. I pannelli della ricerca sono in Ricerche.jsx.
// L'annuncio ritirato si vede qui, nel fascicolo del cliente che ce l'aveva: "Ancora disponibile" chiude il dubbio
// dopo la telefonata all'agenzia (backend/ricontrolli.py).
// Più case della stessa agenzia (backend/agenzia_case.py): sulla card c'è «altre 2 case con Ermes per questo
// cliente» (il clic evidenzia le sorelle) e in cima l'interruttore «Per agenzia», spento di base, che aggiunge i
// riquadri con «Chiama per 3 case». È una vista in più: le card restano nell'ordine di inserimento e il numero
// progressivo non cambia mai, spento la scheda è identica a prima.

// I dati in badge della card: m², locali, bagni, piano, classe, spese (quelli che mancano non ci sono)
const DATI_CARD = ['superficie', 'locali', 'bagni', 'piano', 'classe', 'spese'];
// Accanto a una casa si legge la DICHIARAZIONE (Dichiarata ✓ / In attesa / Non dichiara, utils/scheda.js), non la
// collaborazione: quella è un giudizio nostro e resta nella scheda dell'agenzia. Il campo buono è
// `stato_dichiarato` (solo per lo staff); `stato` è quello che il backend calcola anche per il cliente
// (backend/server.py: stato_agenzia) e vale da riserva.

export const LogoAgenzia = ({ ag }) => {
  const [rotto, setRotto] = useState(false);
  const iniziali = (ag.nome || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
  return (
    <span className="logo" aria-hidden="true">
      {ag.logo_url && !rotto ? <img src={ag.logo_url} alt="" loading="lazy" onError={() => setRotto(true)} /> : iniziali}
    </span>
  );
};

// L'agenzia di una casa come la raggruppa il backend (agenzia_case.chiave_agenzia): l'id se c'è, altrimenti il
// nome scritto sull'annuncio (le agenzie senza scheda esistono). Senza né l'uno né l'altro non si raggruppa.
export const chiaveAgenzia = (i) => {
  if (i.agenzia_id) return i.agenzia_id;
  const nome = String(i.agenzia || i.agenzia_info?.nome || '').trim();
  return nome ? `nome:${nome.toLowerCase()}` : null;
};

// Il nome corto dell'agenzia per l'etichetta: «altre 2 case con Ermes per questo cliente»
const nomeAgenzia = (i) => i.agenzia_info?.nome || i.agenzia_info?.etichetta || i.agenzia || 'questa agenzia';

// «altre 2 case con Ermes per questo cliente»: il conto (altre_con_agenzia) arriva già fatto dal backend e non
// va mai al cliente. Il clic evidenzia le sorelle e porta in vista la prima, un secondo clic toglie tutto.
const AltreCase = ({ i, acceso, onClic }) => {
  const n = i.altre_con_agenzia || 0;
  if (!n) return null;
  return (
    <button type="button" className={`altre-case${acceso ? ' on' : ''}`} data-testid={`altre-case-${i.id}`}
      aria-pressed={acceso} title="Le altre case di questo cliente con la stessa agenzia" onClick={onClic}>
      {n === 1 ? `altra casa con ${nomeAgenzia(i)} per questo cliente` : `altre ${n} case con ${nomeAgenzia(i)} per questo cliente`}
    </button>
  );
};

const AgenziaCard = ({ i, altre = null }) => {
  const ag = i.agenzia_info;
  if (!ag) return i.agenzia ? <div className="agenzia"><span className="logo" aria-hidden="true">?</span><div className="chi"><b>{i.agenzia}</b>{altre}</div></div> : null;
  const dich = dichiarazioneAgenzia(ag.stato_dichiarato || ag.stato);
  return (
    <div className="agenzia" data-testid="agenzia-card">
      <LogoAgenzia ag={ag} />
      <div className="chi">
        <b>{ag.etichetta || [ag.nome, ag.comune].filter(Boolean).join(' · ')}</b>
        <span><i className="pallino" style={{ background: dich.colore }} />{dich.testo}
          {ag.contatto ? ` · ${ag.contatto}` : ''}{ag.telefono ? ` · ${ag.telefono}` : ''}</span>
        {altre}
      </div>
    </div>
  );
};

export const CambiaStato = ({ immobile, onFatto, isAdmin }) => {
  const [scarto, setScarto] = useState(false);
  const [motivo, setMotivo] = useState('prezzo_alto');
  // perché il server non scarta (visita fissata o proposta in corso): scritto accanto a "Scarta", non solo nell'avviso
  const [rifiuto, setRifiuto] = useState(null);
  // un cambio alla volta: doppio clic su "Scarta" mandava due PUT (due "Tolto dalla ricerca" nella storia del cliente)
  const invio = useInvioUnico();
  const [inVolo, setInVolo] = useState(false);
  const [, nome, colore] = statoImmobile(immobile.stato_attivita);
  const salva = async (stato, extra = {}) => {
    if (!invio.blocca()) return;
    setInVolo(true);
    setRifiuto(null);
    try {
      await axios.put(`${API}/immobili/${immobile.id}/stato-attivita`, { stato_attivita: stato, ...extra });
      toast.success('Stato aggiornato');
      setScarto(false);
      onFatto();
    } catch (e) {
      if (stato === 'scartato' && e.response?.status === 400 && typeof e.response.data?.detail === 'string') setRifiuto(e.response.data.detail);
      errore(e);
    } finally {
      invio.sblocca();
      setInVolo(false);
    }
  };
  if (!isAdmin) return <span className="sel"><i style={{ background: colore }} />{nome}</span>;
  return (
    <>
      <span className="sel"><i style={{ background: colore }} />
        <select value={immobile.stato_attivita || 'nuovo'} aria-label="Stato dell'immobile" disabled={inVolo} onChange={(e) => (e.target.value === 'scartato' ? setScarto(true) : salva(e.target.value))}>
          {STATI_IMMOBILE.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </select>▾
      </span>
      {scarto && (
        <span className="form-riga" style={{ marginTop: 0 }}>
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo dello scarto">{MOTIVI_SCARTO.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select>
          <button type="button" className="btn xs p" onClick={() => salva('scartato', { motivo_scarto: motivo })} disabled={inVolo}>Scarta</button>
          <button type="button" className="btn xs" onClick={() => { setScarto(false); setRifiuto(null); }}>No</button>
          {rifiuto && <span role="alert" data-testid="scarto-rifiutato" style={{ color: 'var(--bad)', flexBasis: '100%' }}>{rifiuto}</span>}
        </span>
      )}
    </>
  );
};

// Card grande di un immobile (scheda Immobili dello staff): foto, titolo, prezzo e €/m², dati in badge, agenzia con logo
// e pallino di collaborazione, stato a menu e azioni. numero = posizione nell'ordine di inserimento.
const CardImmobile = ({ i, numero, cliente, onFatto, isAdmin, apri, evidenziata = false, onAltre, altreAccese = false }) => {
  const [esito, setEsito] = useState(false);
  const [compila, setCompila] = useState(false);
  const [elimina, setElimina] = useState(false);
  const spento = i.stato_attivita === 'scartato';
  const daCompilare = i.stato_elaborazione === 'da_compilare';  // l'estrattore non l'ha letto: si compila a mano
  const riprova = async () => {
    try { await axios.post(`${API}/admin/estrazioni/${i.id}/riprova`); toast.success("Rimesso in coda: l'estrattore lo rilegge"); onFatto(); } catch (e) { errore(e); }
  };
  // L'annuncio non c'è più: lo staff chiama l'agenzia e chiude il dubbio. "Ancora disponibile" toglie il segno e vale
  // come un controllo (backend/ricontrolli.py), così la casa può tornare fra quelle da proporre.
  const ancoraDisponibile = async () => {
    try { await axios.post(`${API}/immobili/${i.id}/ancora-disponibile`); toast.success('Segnata come ancora disponibile'); onFatto(); } catch (e) { errore(e); }
  };
  const riga2 = spento ? `Scartato${i.scartato_dal_cliente ? ' dal cliente' : ''}${i.motivo_scarto ? ` · ${(MOTIVI_SCARTO.find((m) => m[0] === i.motivo_scarto) || [])[1] || i.motivo_scarto}` : ''}${i.note_scarto ? ` · “${i.note_scarto}”` : ''}`
    : i.visita_richiesta ? `Visita richiesta dal cliente${i.visita_richiesta.nota ? ` · “${i.visita_richiesta.nota}”` : ''}`
      : venduto(i) ? testoVenduto(i)
      : ritirato(i) ? testoRitirato(i) : daCompilare ? "L'estrattore non l'ha letto · dati da compilare"
        : variazioniPrezzo(i.storico_prezzi)[0]?.testo || i.parere_breve || null;
  const voce = { id: `tel:${i.id}`, tipo: 'telefonata', immobile_id: i.id, agenzia_id: i.agenzia_id, cliente_id: cliente.id, cliente_breve: cliente.nome, tentativi: i.tentativi_contatto || 0 };
  const tel = linkChiama(i.agenzia_info?.telefono);
  const mq = prezzoMq(i);
  const dati = datiImmobile(i).filter((d) => DATI_CARD.includes(d.chiave));
  const aggiunto = leggiDataUtc(i.data_aggiunta);
  const chi = i.inserito_da_tipo === 'cliente' ? 'dal cliente' : i.inserito_da_nome ? `da ${i.inserito_da_nome}` : '';
  const luogo = [i.indirizzo, i.citta].filter((x) => x && !(i.titolo || '').includes(x)).join(', ');
  const visitaRiga = spento ? null : rigaVisita(i);
  return (
    <article className={`casa-st${spento ? ' spento' : ''}${evidenziata ? ' evidenziata' : ''}`} data-testid={`immobile-${i.id}`} id={`immobile-${i.id}`}>
      <Link className="foto" to={`/immobili/${i.id}`} tabIndex={-1} aria-hidden="true">
        {i.immagine_url ? <img src={i.immagine_url} alt="" loading="lazy" /> : <Icona nome="casa" />}
      </Link>
      <div className="testa">
        <div className="quando">N. {numero}{aggiunto ? ` · aggiunto il ${giornoEMese(aggiunto)}` : ''}{chi ? ` ${chi}` : ''}</div>
        <h4>{i.titolo || i.indirizzo || i.link}</h4>
        {luogo && <div className="luogo">{luogo}</div>}
        <div className="prezzi">
          <b>{euro(i.prezzo) || 'Prezzo da capire'}</b>
          {mq && <span data-testid="prezzo-mq">{migliaia(mq)} €/m²</span>}
        </div>
      </div>
      <div className="info">
        {dati.length > 0 && (
          <div className="dati">
            {dati.map((d) => <span key={d.chiave} className="dato"><Icona nome={d.icona} />{d.prima ? `${d.etichetta} ${d.valore}` : `${d.valore} ${d.etichetta}`}</span>)}
          </div>
        )}
        <AgenziaCard i={i} altre={onAltre ? <AltreCase i={i} acceso={altreAccese} onClic={() => onAltre(i)} /> : null} />
        {visitaRiga && (
          <div className={`riga-visita ${visitaRiga.tipo}`} data-testid="riga-visita">
            <Icona nome={visitaRiga.tipo === 'prossima' ? 'calendario' : 'si'} /><b>{visitaRiga.testo}</b>
            {visitaRiga.tipo === 'prossima' && (
              <span className="az">
                <a href={indicazioni(i)} target="_blank" rel="noopener noreferrer">Indicazioni</a>
                <AzioniVisita visita={visitaDa(visitaRiga.visita, i.id)} cliente={cliente} onFatto={onFatto} piccoli />
              </span>
            )}
          </div>
        )}
        {riga2 && <div className={`nota${i.visita_richiesta ? ' ok' : daCompilare || (!spento && (ritirato(i) || venduto(i))) ? ' w' : ''}`}>{riga2}</div>}
      </div>
      <div className="azioni">
        <CambiaStato immobile={i} onFatto={onFatto} isAdmin={isAdmin} />
        {!spento && i.agenzia_id && <button type="button" className={`btn sm${i.visita_richiesta ? ' p' : ''}`} onClick={() => setEsito(!esito)}>Esito telefonata</button>}
        {daCompilare && isAdmin && <button type="button" className="btn sm p" onClick={() => setCompila(true)}>Compila dati</button>}
        {daCompilare && isAdmin && i.estrazione_stato === 'fallito' && <button type="button" className="btn sm" onClick={riprova}>Riprova la lettura</button>}
        {/* «Ancora disponibile» serve a sciogliere un dubbio. Su una casa venduta non c'è dubbio: lo dice il
            portale nei suoi dati strutturati, quindi il bottone non si mostra. */}
        {!spento && ritirato(i) && !venduto(i) && <button type="button" className="btn sm" onClick={ancoraDisponibile}>Ancora disponibile</button>}
        {!spento && !i.prossima_visita && <button type="button" className={`btn sm${i.visita_richiesta ? ' p' : ''}`} onClick={() => apri('visita', i.id)}>Fissa visita</button>}
        {!spento && tel && <a className="btn sm mezzo" href={tel}>Chiama</a>}
        {!spento && <ParereImmobileDialog immobile={i} onSalvato={onFatto} stile="st" />}
        <Link className="btn sm" to={`/immobili/${i.id}`}>Apri</Link>
        {isAdmin && <button type="button" className="btn sm rosso-vuoto" onClick={() => setElimina(true)}>Elimina</button>}
      </div>
      {esito && <EsitoTelefonata voce={voce} isAdmin={isAdmin} nomeCliente={cliente.nome} onFatto={() => { setEsito(false); onFatto(); }} />}
      {compila && <CompilaDati immobile={i} onChiudi={() => setCompila(false)} onCompilato={() => { setCompila(false); onFatto(); }} />}
      {elimina && <Eliminazione tipo="immobile" id={i.id} onChiudi={() => setElimina(false)} onEliminato={() => { setElimina(false); onFatto(); }} />}
    </article>
  );
};

// Un riquadro «Per agenzia»: nome dell'agenzia (con il link alla scheda solo per l'admin), quante case attive,
// referente e telefono, e un solo bottone «Chiama per 3 case» — l'esito multiplo lo apre chi ci monta sopra
// (prop onChiamaGruppo): senza quella prop il bottone non si mostra.
export const GruppoAgenzia = ({ gruppo: g, isAdmin, onChiamaGruppo }) => {
  const ref = g.referente;
  const tel = linkChiama(g.telefono);
  return (
    <div className="gruppo-ag" data-testid={`gruppo-${g.agenzia_id || g.etichetta}`}>
      <div className="chi">
        <b>{isAdmin && g.agenzia_id ? <Link to={`/admin/agenzie/${g.agenzia_id}`}>{g.etichetta}</Link> : g.etichetta}</b>
        <span>
          {g.attive} case attive
          {ref?.nome ? ` · ref. ${ref.nome}${ref.ruolo ? ` (${ref.ruolo})` : ''}` : ''}
          {g.telefono ? <> · {tel ? <a href={tel}>{g.telefono}</a> : g.telefono}</> : ''}
        </span>
        {ref?.nota && <small>{ref.nota}</small>}
      </div>
      {onChiamaGruppo && (
        <button type="button" className="btn sm p" onClick={() => onChiamaGruppo(g)}>{g.chiama || `Chiama per ${g.attive} case`}</button>
      )}
    </div>
  );
};

// Filtri facoltativi della scheda Immobili: tolgono case, non cambiano mai l'ordine
const FILTRI_IMMOBILI = [
  ['tutti', 'Tutti', () => true],
  ['attivi', 'Attivi', (i) => i.stato_attivita !== 'scartato'],
  ['scartati', 'Scartati', (i) => i.stato_attivita === 'scartato'],
];

const TabImmobili = ({ cliente, immobili, onFatto, isAdmin, apri, versione, onChiamaGruppo }) => {
  const [filtro, setFiltro] = useState('tutti');
  // «Per agenzia»: spento di base, acceso aggiunge i riquadri in cima (le card non si toccano)
  const [perAgenzia, setPerAgenzia] = useState(false);
  const [gruppi, setGruppi] = useState(null);       // null = non ancora chiesti (niente avviso mentre arrivano)
  // la casa da cui si guardano «le altre con la stessa agenzia»: {chiave, da}
  const [altre, setAltre] = useState(null);
  // ?evidenzia=<immobile>: arrivando da Oggi, Adesso o agenda, la card di quell'immobile evidenziata e in vista
  const evidenzia = new URLSearchParams(useLocation().search).get('evidenzia');
  const trovato = Boolean(evidenzia && immobili.some((i) => i.id === evidenzia));
  useEffect(() => {
    if (!trovato) return;
    setFiltro('tutti');
    const t = setTimeout(() => document.getElementById(`immobile-${evidenzia}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 150);
    return () => clearTimeout(t);
  }, [evidenzia, trovato]);
  // i gruppi si chiedono solo quando l'interruttore è acceso: spento la scheda fa le stesse chiamate di prima
  useEffect(() => {
    if (!perAgenzia) return undefined;
    let vivo = true;
    axios.get(`${API}/clienti/${cliente.id}/agenzie-case`)
      .then((r) => { if (vivo) setGruppi(Array.isArray(r.data?.gruppi) ? r.data.gruppi : []); })
      .catch(() => { if (vivo) setGruppi([]); });
    return () => { vivo = false; };
  }, [cliente.id, versione, perAgenzia]);
  // ordine di inserimento: il più vecchio in alto, il più recente in fondo; lo stato non sposta la casa
  const ordinati = perInserimento(immobili, (i) => leggiDataUtc(i.data_aggiunta)).map((i, n) => ({ i, numero: n + 1 }));
  const attivi = immobili.filter(FILTRI_IMMOBILI[1][2]);
  const prende = (FILTRI_IMMOBILI.find(([k]) => k === filtro) || FILTRI_IMMOBILI[0])[2];
  const mostrati = ordinati.filter(({ i }) => prende(i));
  // c'è almeno una casa in gruppo? allora ha senso l'interruttore, altrimenti non si mostra nemmeno
  const raggruppabili = immobili.some((i) => (i.altre_con_agenzia || 0) > 0);
  // clic su «altre 2 case con Ermes…»: evidenzia le sorelle e porta in vista la prima; un altro clic toglie tutto
  const vediAltre = (casa) => {
    if (altre?.da === casa.id) { setAltre(null); return; }
    const chiave = chiaveAgenzia(casa);
    setAltre({ chiave, da: casa.id });
    const prima = mostrati.find(({ i }) => i.id !== casa.id && chiaveAgenzia(i) === chiave);
    if (prima) document.getElementById(`immobile-${prima.i.id}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };
  const sorella = (i) => Boolean(altre && i.id !== altre.da && chiaveAgenzia(i) === altre.chiave);
  return (
    <>
    <AnnunciNuovi cliente={cliente} versione={versione} onFatto={onFatto} isAdmin={isAdmin} />
    <CaseInArchivio cliente={cliente} versione={versione} onFatto={onFatto} />
    <section className="card r">
      <div className="ttl"><div className="i b"><Icona nome="immobili" /></div><h2>Immobili <small>{immobili.length} · {attivi.length} attivi</small></h2>
        <button type="button" className="btn sm p" onClick={() => apri('link')}>+ Aggiungi link</button></div>
      <CaseCondivise cliente={cliente} versione={versione} />
      {!immobili.length && <p className="vuoto">Ancora nessun immobile.</p>}
      {immobili.length > 0 && (
        <div className="casa-filtri" role="group" aria-label="Quali immobili">
          {FILTRI_IMMOBILI.map(([k, nome, prendi]) => (
            <button type="button" key={k} className={`chip${filtro === k ? ' on' : ''}`} aria-pressed={filtro === k} onClick={() => setFiltro(k)}>
              {nome} <b>{immobili.filter(prendi).length}</b>
            </button>
          ))}
          {raggruppabili && (
            <label className="per-agenzia">
              <input type="checkbox" data-testid="per-agenzia" checked={perAgenzia} onChange={(e) => setPerAgenzia(e.target.checked)} />
              Per agenzia
            </label>
          )}
          <span className="ordine">Dal primo inserito all'ultimo</span>
        </div>
      )}
      {perAgenzia && gruppi?.length > 0 && (
        <div className="gruppi-ag" data-testid="gruppi-agenzia">
          {gruppi.map((g) => <GruppoAgenzia key={g.agenzia_id || g.etichetta} gruppo={g} isAdmin={isAdmin} onChiamaGruppo={onChiamaGruppo} />)}
        </div>
      )}
      {perAgenzia && gruppi && !gruppi.length && <p className="vuoto">Nessuna agenzia con più di una casa attiva per questo cliente.</p>}
      {immobili.length > 0 && !mostrati.length && <p className="vuoto">Nessun immobile qui.</p>}
      <div className="case-st">
        {mostrati.map(({ i, numero }) => (
          <CardImmobile key={i.id} i={i} numero={numero} cliente={cliente} onFatto={onFatto} isAdmin={isAdmin} apri={apri}
            evidenziata={i.id === evidenzia || sorella(i)} onAltre={vediAltre} altreAccese={altre?.da === i.id} />
        ))}
      </div>
    </section>
    <RicercheAutomatiche cliente={cliente} versione={versione} />
    </>
  );
};

export default TabImmobili;
